import translate from "@/i18n/translate";
import "../lightcast/Lightcast.scss";
import { useEffect, useReducer } from "react";
import { sendRequest } from "@/common/http";
import {
  PopulationTrends,
  JobTrends,
  LaborForceTrends,
  UnemploymentTrends
} from "../lightcast/Trends";
import BottomGrowingIndustries from "../lightcast/industries/BottomGrowingIndustries";
import TopGrowingIndustries from "../lightcast/industries/TopGrowingIndustries";
import BottomGrowingOccupations from "../lightcast/occupations/BottomGrowingOccupations";
import TopGrowingOccupations from "../lightcast/occupations/TopGrowingOccupations";
import PopulationByAge from "../lightcast/population/PopulationByAge";
import PopulationByRaceEthnicity from "../lightcast/population/PopulationByRaceEthnicity";

import {
  LaborForceTrendsInterface,
  IndustryOrOccupation,
  IndustriesOrOccupations,
  PopulationTrendsInterface,
  JobTrendsInterface,
  LaborForceTrend,
  LaborForceBreakdown,
  Timeframe
} from "../lightcast/Interface";
import { AnalyticView, AnalyticViewTable } from "../lightcast/LaborForce";
import ApiError from "../common/elements/ApiError";
export interface TrendsProps {
  setLaborForceBreakdown: (lfb: LaborForceBreakdown) => void;
  onComponentLoad: (component: string) => void;
  laborForceBreakdown: LaborForceBreakdown;
}

interface TrendsState {
  populationTrends: PopulationTrendsInterface | null;
  jobTrends: JobTrendsInterface | null;
  laborForceTrends: LaborForceTrend | null;
  timeframe: Timeframe | null;
  bottomGrowingIndustries: IndustryOrOccupation[] | null;
  topGrowingIndustries: IndustryOrOccupation[] | null;
  bottomGrowingOccupations: IndustryOrOccupation[] | null;
  topGrowingOccupations: IndustryOrOccupation[] | null;
  loader: boolean;
  lfbApiError: boolean;
  occupationsApiError: boolean;
  industriesApiError: boolean;
}

interface TrendsAction {
  type: string;
  laborForceTrendsInterface: LaborForceTrendsInterface;
  industries: IndustriesOrOccupations;
  occupations: IndustriesOrOccupations;
}

const defaultIndustriesOrOccupations = {
  top: [],
  bottom: []
};

const defaultJobTrends = {
  JobsNationalRateGrowthIncrease: true,
  jobsFromTotalCount: 0,
  jobsGrowthIncrease: true,
  jobsNationalRatePercentage: 0,
  jobsPercentage: 0,
  jobsToTotalCount: 0
};

const defaultLaborForceBreakdown = {
  civilianPopulation: "",
  civilianPopulationPercentage: 0,
  employed: "",
  employedPercentage: 0,
  laborForcePopulation: "",
  laborForcePopulationPercentage: 0,
  notInLaborForce: "",
  notInLaborForcePercentage: 0,
  totalPopulation: "",
  under16: "",
  under16Percentage: 0,
  unemployed: "",
  unemployedPercentage: 0
};

const defaultLaborForceTrends = {
  laborForceGrowthIncrease: true,
  laborForceGrowthPercentageFrom: 0,
  laborForceGrowthPercentageTo: 0,
  laborForcePercentage: 0,
  unemploymentGrowthIncrease: true,
  unemploymentGrowthPercentageFrom: 0,
  unemploymentGrowthPercentageTo: 0,
  unemploymentPercentage: 0
};

const defaultPopulationTrends = {
  change: 0,
  changePercentage: 0,
  inclined: true,
  totalPopulation: 0
};

const defaultTimeframe = {
  currentYear: null,
  pastYear: null
};

const trendsDefaultAction = {
  type: "",
  laborForceTrendsInterface: {
    jobTrends: defaultJobTrends,
    laborForceBreakdown: defaultLaborForceBreakdown,
    laborForceTrends: defaultLaborForceTrends,
    populationTrends: defaultPopulationTrends,
    timeframe: defaultTimeframe
  },
  industries: defaultIndustriesOrOccupations,
  occupations: defaultIndustriesOrOccupations
};

const reducer = (state: TrendsState, action: TrendsAction) => {
  switch (action.type) {
    case "setLaborForceBreakdownAndTrends":
      return {
        ...state,
        populationTrends: action.laborForceTrendsInterface.populationTrends,
        jobTrends: action.laborForceTrendsInterface.jobTrends,
        laborForceTrends: action.laborForceTrendsInterface.laborForceTrends,
        timeframe: action.laborForceTrendsInterface.timeframe
      };

    case "setIndustries":
      return {
        ...state,
        bottomGrowingIndustries: action.industries?.bottom,
        topGrowingIndustries: action.industries?.top,
        loader: false
      };
    case "setOccupations":
      return {
        ...state,
        bottomGrowingOccupations: action.occupations?.bottom,
        topGrowingOccupations: action.occupations?.top,
        loader: false
      };

    case "setLaborForceBreakdownAndTrendsApiError":
      return { ...state, lfbApiError: true };

    case "setIndustriesApiError":
      return { ...state, industriesApiError: true, loader: false };

    case "setOccupationsApiError":
      return { ...state, occupationsApiError: true, loader: false };

    default:
      return state;
  }
};

const initialState = {
  populationTrends: null,
  jobTrends: null,
  laborForceTrends: null,
  timeframe: null,
  bottomGrowingIndustries: null,
  topGrowingIndustries: null,
  bottomGrowingOccupations: null,
  topGrowingOccupations: null,
  loader: true,
  lfbApiError: false,
  occupationsApiError: false,
  industriesApiError: false
};

const onTabLoad = (tab: string) => {};

function setTooglePosition(event: any, chartId: string) {
  var chartBodyRect: any;
  chartBodyRect = document
    .querySelector("." + chartId)
    ?.getBoundingClientRect();
  const targetRect = event.target.getBoundingClientRect();
  const availableSpaceLeft = targetRect.left - chartBodyRect?.left;
  const availableSpaceRight = chartBodyRect?.right - targetRect.right;
  if (window.innerWidth >= 1024) {
    event.target.childNodes[0].style.width = "180px";
  } else {
    event.target.childNodes[0].style.width = "160px";
  }
  if (availableSpaceLeft > availableSpaceRight) {
    event.target.childNodes[0].style.right = "30%";
    event.target.childNodes[0].style.removeProperty("left");
  } else {
    event.target.childNodes[0].style.left = "10%";
    event.target.childNodes[0].style.removeProperty("right");
  }
}

export default function TrendsExport({
  setLaborForceBreakdown,
  onComponentLoad,
  laborForceBreakdown
}: TrendsProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  useEffect(() => {
    const applyResult = (result: LaborForceTrendsInterface) => {
      dispatch({
        ...trendsDefaultAction,
        type: "setLaborForceBreakdownAndTrends",
        laborForceTrendsInterface: result
      });
      setLaborForceBreakdown(result.laborForceBreakdown);
      onComponentLoad("trends");
    };

    const onError = () => {
      dispatch({
        ...trendsDefaultAction,
        type: "setLaborForceBreakdownAndTrendsApiError"
      });
      onComponentLoad("trends");
    };

    sendRequest(
      { uri: "/lightcast/labor_force_breakdown_and_trends", camelize: true },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    const applyResult = (result: IndustriesOrOccupations) => {
      dispatch({
        ...trendsDefaultAction,
        type: "setIndustries",
        industries: result
      });
      onComponentLoad("industries");
    };

    const onError = () => {
      dispatch({
        ...trendsDefaultAction,
        type: "setIndustriesApiError"
      });
      onComponentLoad("industries");
    };

    sendRequest(
      { uri: "/lightcast/industries", camelize: true },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    const applyResult = (result: IndustriesOrOccupations) => {
      dispatch({
        ...trendsDefaultAction,
        type: "setOccupations",
        occupations: result
      });
      onComponentLoad("occupations");
    };

    const onError = () => {
      dispatch({
        ...trendsDefaultAction,
        type: "setOccupationsApiError"
      });
      onComponentLoad("occupations");
    };

    sendRequest(
      { uri: "/lightcast/occupations", camelize: true },
      applyResult,
      onError
    );
  }, []);

  return (
    <div className="">
      <div className="header">
        <div className="info">
          <h3 className="title trends-title">
            {translate("app.trends.trendsWidgetTitle")}
          </h3>
          <p className="description -des trends-desc">
            {translate("app.trends.trendsWidgetDescription")}
          </p>
        </div>
      </div>
      {!state.lfbApiError ? (
        <div className="body wm-grid-row trends-widget trends-body">
          <PopulationTrends
            populationTrends={state.populationTrends}
            timeframe={state.timeframe}
          />
          <JobTrends jobTrends={state.jobTrends} timeframe={state.timeframe} />
          <LaborForceTrends
            laborForceTrends={state.laborForceTrends}
            timeframe={state.timeframe}
          />

          <UnemploymentTrends
            laborForceTrends={state.laborForceTrends}
            timeframe={state.timeframe}
          />
        </div>
      ) : (
        <ApiError />
      )}
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <div className="grid-container change-in-population">
        <PopulationByAge
          setTooglePosition={setTooglePosition}
          onTabLoad={onTabLoad}
          onComponentLoad={onComponentLoad}
          showIntegratedHeader={false}
        ></PopulationByAge>
      </div>
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <div className="grid-container change-in-population">
        <PopulationByRaceEthnicity
          setTooglePosition={setTooglePosition}
          onTabLoad={onTabLoad}
          onComponentLoad={onComponentLoad}
          showIntegratedHeader={false}
        ></PopulationByRaceEthnicity>
      </div>
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <div className="header">
        <div className="info" id="labor-force-info">
          <h3 className="title html-title">
            {translate("app.lightcast.laborForce.headingLabel")}
          </h3>
          <div className="description -labor-des html-desc">
            {translate("app.lightcast.laborForce.helperLabel")}
          </div>
        </div>
      </div>
      {!state.lfbApiError ? (
        <>
          {laborForceBreakdown && (
            <div className="analytic-view-container">
              <AnalyticView laborForceBreakdown={laborForceBreakdown} />
              <AnalyticViewTable
                laborForceBreakdown={laborForceBreakdown}
                forHtml={true}
              />
            </div>
          )}
        </>
      ) : (
        <div id="api-error">
          <ApiError />
        </div>
      )}
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <BottomGrowingIndustries
        industries={state.bottomGrowingIndustries}
        loader={state.loader}
        forHtml={true}
      />
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <TopGrowingIndustries
        industries={state.topGrowingIndustries}
        loader={state.loader}
        forHtml={true}
      />
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <BottomGrowingOccupations
        occupations={state.bottomGrowingOccupations}
        loader={state.loader}
        forHtml={true}
      />
      <LightcastSource />
      <div className="widget-border-margin"></div>
      <TopGrowingOccupations
        occupations={state.topGrowingOccupations}
        loader={state.loader}
        forHtml={true}
      />
      <LightcastSource />
    </div>
  );
}

const LightcastSource = () => {
  return (
    <div className="source-margin">{translate("app.lightcast.Source")}</div>
  );
};
